import type { FieldValues, Path } from "react-hook-form";

import {
  FieldError,
  Input,
  Label,
  ListBox,
  Select,
  TextArea,
  TextField,
} from "@heroui/react";
import { useController, useFormContext } from "react-hook-form";

/** A multi-line text field for the react-hook-form forms (the kit has single-line fields only). */
export function TextAreaField<T extends FieldValues>({
  name,
  label,
  placeholder,
  rows = 3,
  dir,
  isRequired,
}: {
  name: Path<T>;
  label: string;
  placeholder?: string;
  rows?: number;
  dir?: "rtl" | "ltr";
  isRequired?: boolean;
}) {
  const { control } = useFormContext<T>();
  const { field, fieldState } = useController({ control, name });

  return (
    <TextField
      className="flex flex-col gap-1.5"
      isInvalid={fieldState.invalid}
      isRequired={isRequired}
      name={field.name}
      value={(field.value as string | undefined) ?? ""}
      onBlur={field.onBlur}
      onChange={field.onChange}
    >
      <Label>{label}</Label>
      <TextArea
        ref={field.ref}
        dir={dir}
        lang={dir === "rtl" ? "ar" : undefined}
        placeholder={placeholder}
        rows={rows}
        variant="secondary"
      />
      <FieldError>{fieldState.error?.message}</FieldError>
    </TextField>
  );
}

/** A number box whose empty value is `undefined`. */
export function OptionalNumberField<T extends FieldValues>({
  name,
  label,
  placeholder,
}: {
  name: Path<T>;
  label: string;
  placeholder?: string;
}) {
  const { control } = useFormContext<T>();
  const { field } = useController({ control, name });
  const value = field.value as number | undefined;

  return (
    <TextField
      className="flex flex-col gap-1.5"
      name={field.name}
      type="number"
      value={value === undefined ? "" : String(value)}
      onBlur={field.onBlur}
      onChange={(text) =>
        field.onChange(text === "" ? undefined : Number(text))
      }
    >
      <Label>{label}</Label>
      <Input
        ref={field.ref}
        inputMode="decimal"
        min={0}
        placeholder={placeholder}
        step="any"
        variant="secondary"
      />
    </TextField>
  );
}

/** A select that can also be left empty ("None"), stored as an empty string. */
export function OptionalSelectField<T extends FieldValues>({
  name,
  label,
  options,
  placeholder,
}: {
  name: Path<T>;
  label: string;
  options: Array<{ id: string; name: string }>;
  placeholder: string;
}) {
  const { watch, setValue } = useFormContext<T>();
  const value = (watch(name) as string | undefined) || "";

  return (
    <Select
      className="flex flex-col gap-1.5"
      placeholder={placeholder}
      selectedKey={value || "__none"}
      variant="secondary"
      onSelectionChange={(key) =>
        setValue(name, (key === "__none" ? "" : String(key)) as never, {
          shouldDirty: true,
        })
      }
    >
      <Label>{label}</Label>
      <Select.Trigger>
        <Select.Value />
        <Select.Indicator />
      </Select.Trigger>
      <Select.Popover>
        <ListBox>
          {[{ id: "__none", name: "None" }, ...options].map((option) => (
            <ListBox.Item key={option.id} id={option.id} textValue={option.name}>
              {option.name}
              <ListBox.ItemIndicator />
            </ListBox.Item>
          ))}
        </ListBox>
      </Select.Popover>
    </Select>
  );
}
